/**
 * Agent Registry – Dubai Police 999 Emergency Assistant
 *
 * 10-agent intent-first flow:
 *
 * greeting_agent → intent_agent ─┬→ inquiry_agent → image_upload_agent → agency_agent → incident_card_agent → closing_agent
 *                                 ├→ followup_agent → intent_agent (clarification loop)
 *                                 └→ human_handoff_agent → closing_agent
 * closing_agent → intent_agent (loop, labels preserved)
 * closing_agent → session end
 */

import { AgentNode, VoiceGender } from './types';
import {
  createGreetingAgent,
  createIntentAgent,
  createFollowUpAgent,
  createInquiryAgent,
  createImageUploadAgent,
  createAgencyAgent,
  createIncidentCardAgent,
  createClosingAgent,
  createHumanHandoffAgent,
} from './nodes';

export function createAllAgents(voiceGender: VoiceGender = 'male'): Map<string, AgentNode> {
  const agents = new Map<string, AgentNode>();

  // greeting_agent is first — it is the entry point
  agents.set('greeting_agent', createGreetingAgent(voiceGender));
  agents.set('intent_agent', createIntentAgent(voiceGender));
  agents.set('followup_agent', createFollowUpAgent(voiceGender));
  agents.set('inquiry_agent', createInquiryAgent(voiceGender));
  agents.set('image_upload_agent', createImageUploadAgent(voiceGender));
  agents.set('agency_agent', createAgencyAgent(voiceGender));
  agents.set('incident_card_agent', createIncidentCardAgent(voiceGender));
  agents.set('closing_agent', createClosingAgent(voiceGender));
  agents.set('human_handoff_agent', createHumanHandoffAgent(voiceGender));

  return agents;
}

export function getInitialAgentId(): string {
  return 'greeting_agent';
}

export function getAllAgentIds(): string[] {
  return [
    'greeting_agent',
    'intent_agent',
    'followup_agent',
    'inquiry_agent',
    'image_upload_agent',
    'agency_agent',
    'incident_card_agent',
    'closing_agent',
    'human_handoff_agent',
  ];
}

export const agentFlowMetadata = {
  entryPoint: 'greeting_agent',
  flows: {
    greeting:     { agents: ['greeting_agent'],      description: 'Welcome message in user language' },
    intent:       { agents: ['intent_agent'],         description: 'Intent classification — routes without answering' },
    followup:     { agents: ['followup_agent'],       description: 'Clarifying questions for ambiguous intent' },
    inquiry:      { agents: ['inquiry_agent'],        description: 'KB search and direct answer — no follow-ups' },
    imageUpload:  { agents: ['image_upload_agent'],   description: 'Evidence photo/video collection' },
    agency:       { agents: ['agency_agent'],         description: 'Emergency agency consent collection' },
    incidentCard: { agents: ['incident_card_agent'],  description: 'Incident number card + calming message' },
    closing:      { agents: ['closing_agent'],        description: 'Wrap-up and new question routing' },
    humanHandoff: { agents: ['human_handoff_agent'],  description: 'Human contact request' },
  },
  transitions: [
    { from: 'greeting_agent',     to: 'intent_agent',       trigger: 'proceed_to_intent' },
    { from: 'intent_agent',       to: 'inquiry_agent',      trigger: 'route_to_inquiry' },
    { from: 'intent_agent',       to: 'image_upload_agent', trigger: 'route_to_crisis' },
    { from: 'intent_agent',       to: 'human_handoff_agent',trigger: 'route_to_handoff' },
    { from: 'intent_agent',       to: 'followup_agent',     trigger: 'request_followup' },
    { from: 'followup_agent',     to: 'intent_agent',       trigger: 'followup_complete' },
    { from: 'inquiry_agent',      to: 'image_upload_agent', trigger: 'done_answering (with agencies)' },
    { from: 'inquiry_agent',      to: 'closing_agent',      trigger: 'done_answering (no agencies)' },
    { from: 'image_upload_agent', to: 'agency_agent',       trigger: 'proceed_to_agencies (normal)' },
    { from: 'image_upload_agent', to: 'incident_card_agent',trigger: 'proceed_to_agencies (crisis)' },
    { from: 'agency_agent',       to: 'incident_card_agent',trigger: 'move_to_incident_card' },
    { from: 'incident_card_agent',to: 'closing_agent',      trigger: 'proceed_to_closing' },
    { from: 'human_handoff_agent',to: 'closing_agent',      trigger: 'move_to_closing' },
    { from: 'closing_agent',      to: 'intent_agent',       trigger: 'route_to_intent (loop)' },
  ],
};

export function validateRegistry(agents: Map<string, AgentNode>): string[] {
  const errors: string[] = [];
  const agentIds = new Set(agents.keys());
  for (const [agentId, agent] of agents) {
    for (const [transitionName, transition] of agent.exitGuardrails.transitions) {
      if (!agentIds.has(transition.target.agentId)) {
        errors.push(`Agent '${agentId}' transition '${transitionName}' → unknown agent '${transition.target.agentId}'`);
      }
    }
    if (agent.exitGuardrails.fallback && !agentIds.has(agent.exitGuardrails.fallback.agentId)) {
      errors.push(`Agent '${agentId}' fallback → unknown agent '${agent.exitGuardrails.fallback.agentId}'`);
    }
  }
  return errors;
}
